import React, { useState, useCallback, useMemo } from 'react';
import { Button, Card, CardBody, CardHeader, CardTitle } from 'reactstrap';
import { Loading, Icon } from 'js/components';
import { AddMemberForm, EditMemberForm } from './MemberForm';
import ConfirmModal from 'js/components/modal/ConfirmModal';
import { mapMembersToCohortList } from 'js/utils/cohort-utils';
import styles from './UserGroupMembers.module.scss';
import MemberGroup from './MemberGroup';
import { useNotify } from 'js/hooks/useNotify';

export function UserGroupMembers({
  userGroup,
  members = [],
  // isLoading,
  hasLoaded,
  onAddMember = () => {},
  onUpdateMember = () => {},
  onRemoveMember = () => {},
}) {
  const notify = useNotify();
  const [isAddingMember, setIsAddingMember] = useState(false);

  const toggleAddMember = useCallback(() => {
    setIsAddingMember(!isAddingMember);
  }, [isAddingMember]);

  const handleAddMemberClick = useCallback(() => {
    setIsAddingMember(true);
  }, []);

  const handleAddMember = useCallback(
    (member) => {
      onAddMember(member);
      setIsAddingMember(false);
    },
    [onAddMember]
  );

  const cohorts = useMemo(() => mapMembersToCohortList(members), [members]);

  const [editingMember, setEditingMember] = useState(null);
  const handleEditMember = useCallback((member) => {
    setEditingMember(member);
    setIsAddingMember(false);
  }, []);

  const handleUpdateMember = useCallback(
    (values) => {
      onUpdateMember(editingMember, values);
      setEditingMember(null);
    },
    [editingMember, onUpdateMember]
  );

  const handleCancelEditMember = useCallback(() => {
    setEditingMember(null);
  }, []);

  const [toRemoveMember, setToRemoveMember] = useState(null);
  const handleRemoveMember = useCallback((member) => {
    setToRemoveMember(member);
  }, []);
  const handleRemoveMemberConfirm = useCallback(async () => {
    try {
      await onRemoveMember(toRemoveMember);
      setToRemoveMember(null);
      setEditingMember(null);
      notify({ type: 'success', message: 'Member removed' });
    } catch (error) {
      notify({ type: 'danger', message: 'Failed to remove member' });
    }
  }, [toRemoveMember, onRemoveMember, notify]);

  return (
    <Card>
      <CardHeader>
        <div className="action-items">
          <Button color="primary" onClick={handleAddMemberClick}>
            <Icon icon="plus" className="btn-icon-left" />
            Add Member
          </Button>
        </div>
        <CardTitle tag="h5">Members</CardTitle>
      </CardHeader>
      <CardBody>
        {isAddingMember && (
          <AddMemberForm
            onAddMember={handleAddMember}
            onCancel={() => setIsAddingMember(false)}
          />
        )}
        {hasLoaded && (
          <div className={styles['user-group-members']}>
            {members.length > 0 ? (
              <div className={styles['member-groups']}>
                {cohorts.map((cohort) => (
                  <MemberGroup
                    key={cohort.cohort ?? '🥕'}
                    group={cohort}
                    showRole={cohorts.length > 1}
                    onEditMember={handleEditMember}
                  />
                ))}
              </div>
            ) : (
              !isAddingMember && (
                <div className="media text-muted">
                  <Icon
                    icon={['fas', 'users']}
                    size="4x"
                    className="mr-3"
                    fixedWidth
                  />
                  <div className="media-body">
                    <p>You haven't added any members yet.</p>
                    <p>
                      Adding members to your user group lets you track their
                      responses over time to see changes in their sentiment. You
                      can also assign members to cohorts to identify related
                      personas.
                    </p>
                    <p>
                      <Button color="primary" onClick={toggleAddMember}>
                        <Icon icon="plus" className="btn-icon-left" />
                        Add some members now
                      </Button>
                    </p>
                  </div>
                </div>
              )
            )}
          </div>
        )}
        {!hasLoaded && <Loading />}
      </CardBody>
      {editingMember && !toRemoveMember && (
        <EditMemberForm
          member={editingMember}
          onSubmit={handleUpdateMember}
          onCancel={handleCancelEditMember}
          onRemove={handleRemoveMember}
        />
      )}
      {toRemoveMember && (
        <ConfirmModal
          isOpen={!!toRemoveMember}
          title="Remove member?"
          onConfirm={handleRemoveMemberConfirm}
          onCancel={() => setToRemoveMember(null)}
          confirmButtonLabel="Remove Member"
        >
          <p>
            Please confirm that you want to remove{' '}
            <strong>
              {toRemoveMember?.user_group_member.name ??
                toRemoveMember?.user_group_member.email}
            </strong>{' '}
            from the user group: <strong>{userGroup.name}.</strong>
          </p>
        </ConfirmModal>
      )}
    </Card>
  );
}

export default UserGroupMembers;
